import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { create } from 'zustand';

/** How long the banner stays on screen (ms). */
const NOTICE_DURATION_MS = 8000;

/**
 * TaxSettingsNotice — the amber "your tax settings were refreshed" banner.
 *
 * Shown (identically) across all four tax-refresh flows: invoice edit, estimate edit, estimate
 * duplication, and estimate→invoice conversion. This component is styling only — WHEN and WHY it
 * appears is decided by the callers; they pass the message. Wording is expected to change once the
 * client confirms, so it is a plain prop.
 */
export const TaxSettingsNotice = ({ message }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            borderRadius: 'var(--borderRadius, 4px)',
            background: '#FBCD76',
            boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.15)',
            padding: '16px',
            width: 'max-content',
            maxWidth: '90vw',
        }}
    >
        <ErrorRoundedIcon sx={{ color: '#956200', fontSize: 20, flexShrink: 0, mt: '2px' }} />
        <Typography
            sx={{
                color: '#432C00',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '150%',
                letterSpacing: '0.15px',
            }}
        >
            {message}
        </Typography>
    </Box>
);

/**
 * Tiny store driving the banner. Deliberately NOT a sonner toast — sonner's fixed-width toast
 * wrapper fights our `width: max-content` once the message wraps. This is our own overlay instead.
 */
const DEFAULT_NOTICE_DURATION_MS = 8000;

const useTaxNoticeStore = create((set) => ({
    message: null,
    duration: DEFAULT_NOTICE_DURATION_MS,

    show: (message, duration = DEFAULT_NOTICE_DURATION_MS) =>
        set({
            message,
            duration,
        }),

    hide: () =>
        set({
            message: null,
            duration: DEFAULT_NOTICE_DURATION_MS,
        }),
}));

/**
 * Fire the notice. Callers keep using this exactly as before; it now drives our own overlay
 * (rendered once by <TaxSettingsNoticeHost /> in App). Auto-dismisses after NOTICE_DURATION_MS.
 */
export const showTaxSettingsNotice = (message, duration = DEFAULT_NOTICE_DURATION_MS) => {
    useTaxNoticeStore.getState().show(message, duration);
};

/**
 * Host — mount ONCE (in App). Renders the banner screen-centered at the top, on its own fixed
 * overlay so nothing constrains its width, and auto-hides after the duration. Click to dismiss early.
 */
export const TaxSettingsNoticeHost = () => {
    const message = useTaxNoticeStore((s) => s.message);
    const duration = useTaxNoticeStore((s) => s.duration);
    const hide = useTaxNoticeStore((s) => s.hide);

    useEffect(() => {
        if (!message) return undefined;
        const timer = setTimeout(hide, duration);
        // A new message restarts the timer, so the latest notice always gets its full duration.
        return () => clearTimeout(timer);
    }, [message, hide]);

    if (!message) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 32,
                left: 0,
                right: 0,
                zIndex: 20000,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
            }}
        >
            <Box sx={{ pointerEvents: 'auto', cursor: 'pointer' }} onClick={hide}>
                <TaxSettingsNotice message={message} />
            </Box>
        </Box>
    );
};

export default TaxSettingsNotice;
